import React, { useMemo } from 'react';
import { Stack, Tooltip } from '@mui/material';
import { Link } from 'react-router-dom';
import { useReportSubscriptions } from '../../subscriptions/reportOrder';
import { formatDateOnly } from '../../Formatters';
import { LicenseOverviewModel } from './ColumnDefinitions';
import { ISettings } from './ColumnDefinitions';
import { Info } from '@mui/icons-material';
import { DataGridPro, GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';

interface LicenseTableProps {
  items: ISettings[];
  licenses: LicenseOverviewModel[];
  personName?: any;
  loading?: boolean;
  rowCount?: number;
  paginationModel?: GridPaginationModel;
  setPaginationModel?(val: GridPaginationModel): void;
}

export function LicenseTable({ personName, items, licenses, loading, rowCount, paginationModel, setPaginationModel }: LicenseTableProps) {
  void personName;
  const { shortNameOf } = useReportSubscriptions();
  const isServerPagination = Boolean(paginationModel && setPaginationModel);

  type LicenseGridRow = {
    id: number;
    companyName: string;
    orgNumber: string;
    customerNumber: string;
    administrator: string;
    createdAt: string;
    invoiceRef: string;
    address1: string;
    subscriptions: string;
    isGodkjentVaatromsbedrift: string;
    usersPerSubscription: string;
    licensesPerSubscription: string;
    industryCode: string;
    numberOfEmployees: number | '';
    postalcode: string;
    country: string;
    totalPageViews: number | '';
    lockedAt: string;
    adminEmail: string;
    renewalDate: string;
  };

  const rows = useMemo<LicenseGridRow[]>(
    () =>
      licenses.map((x) => ({
        id: x.id,
        companyName: x.companyName ? x.companyName : '<Mangler navn>',
        orgNumber: x.orgNumber,
        customerNumber: x.customerNumber,
        administrator: x.administrator,
        createdAt: formatDateOnly(x.createdAt),
        invoiceRef: x.invoiceRef ?? '',
        address1: x.address?.address1 ?? '',
        subscriptions: x.subscriptions?.map((c) => shortNameOf(c)).join(',') ?? '',
        isGodkjentVaatromsbedrift: x.isGodkjentVaatromsbedrift === null ? 'Mangler data' : x.isGodkjentVaatromsbedrift ? 'Ja' : 'Nei',
        usersPerSubscription: x.usersPerSubscriptions?.map((c) => shortNameOf(c.subscriptionId) + ': ' + c.numberOfUsers).join(', ') ?? '',
        licensesPerSubscription: x.licensesPerSubscription?.map((c) => shortNameOf(c.subscriptionId) + ': ' + c.numberOfUsers).join(', ') ?? '',
        industryCode: x.code !== null && x.code !== undefined ? x.code + ',' + (x.codeName ?? '') : '',
        numberOfEmployees: x.numberOfEmployees ?? '',
        postalcode: x.address?.postalCode ?? '',
        country: x.address?.country ?? '',
        totalPageViews: x.totalPageViews ?? '',
        lockedAt: x.lockedAt ?? '',
        adminEmail: x.adminEmail ?? '',
        renewalDate: formatDateOnly(x.renewalDate),
      })),
    [licenses, shortNameOf],
  );

  const columns = useMemo<GridColDef<LicenseGridRow>[]>(() => {
    return items
      .filter((c) => c.showColumn)
      .map((item) => {
        const column: GridColDef<LicenseGridRow> = {
          field: item.columnName,
          headerName: item.name,
          minWidth: 140,
          flex: 1,
          sortable: true,
          renderHeader: () => (
            <Stack direction="row" spacing={0.5} sx={{ whiteSpace: 'pre-wrap', alignItems: 'center' }}>
              <span style={{ fontWeight: 700 }}>{item.name}</span>
              {item.infoText !== '' && (
                <Tooltip title={<div style={{ whiteSpace: 'pre-wrap' }}>{item.infoText}</div>}>
                  <Info style={{ fontSize: 14 }} />
                </Tooltip>
              )}
            </Stack>
          ),
        };

        if (item.columnName === 'companyName') {
          column.renderCell = (params) => <Link to={'/license/' + params.row.id}>{params.value}</Link>;
          column.minWidth = 220;
        }

        return column;
      });
  }, [items]);

  return (
    <DataGridPro
      autoHeight
      density="compact"
      rows={rows}
      columns={columns}
      localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
      pagination
      paginationMode={isServerPagination ? 'server' : 'client'}
      loading={loading ?? false}
      rowCount={isServerPagination ? rowCount : undefined}
      paginationModel={isServerPagination ? paginationModel : undefined}
      onPaginationModelChange={isServerPagination ? setPaginationModel : undefined}
      pageSizeOptions={[50, 100, 250]}
      initialState={
        isServerPagination
          ? undefined
          : {
              pagination: {
                paginationModel: {
                  pageSize: 50,
                  page: 0,
                },
              },
            }
      }
      sx={{
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 700,
        },
      }}
    />
  );
}
